/**
 * Client-side geolocation utilities
 * Provides browser-based geolocation as a fallback when IP detection fails
 */

/**
 * Get user's location using browser geolocation API
 * @returns {Promise<{latlng: [number, number], cityState: string|null}>}
 */
export async function getBrowserGeolocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser"))
      return
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds
      maximumAge: 300000 // 5 minutes - use cached location if available
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude
        const lng = position.coords.longitude

        try {
          // Try to reverse geocode to get city/state
          const cityState = await reverseGeocode(lat, lng)
          resolve({
            latlng: [lat, lng],
            cityState
          })
        } catch (error) {
          // If reverse geocoding fails, still return coordinates
          resolve({
            latlng: [lat, lng],
            cityState: null
          })
        }
      },
      (error) => {
        reject(error)
      },
      options
    )
  })
}

/**
 * Reverse geocode coordinates to get city and state
 * Uses Google Maps Geocoding API if available, otherwise falls back to a simple API
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<string|null>}
 */
async function reverseGeocode(lat, lng) {
  // Try Google Maps Geocoding API if available
  if (window.google?.maps?.Geocoder) {
    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder()
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results && results.length > 0) {
          const result = results[0]
          const cityComponent = result.address_components.find((component) => component.types.includes("locality"))
          const stateComponent = result.address_components.find((component) =>
            component.types.includes("administrative_area_level_1")
          )

          if (cityComponent && stateComponent) {
            const city = cityComponent.long_name
            const state = stateComponent.short_name || stateComponent.long_name
            resolve(`${city}, ${state}`)
            return
          }
        }
        resolve(null)
      })
    })
  }

  // Fallback: Use a free reverse geocoding service
  try {
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
    )
    if (response.ok) {
      const data = await response.json()
      if (data.city && data.principalSubdivision) {
        // Extract state abbreviation if available
        const state = data.principalSubdivisionCode || data.principalSubdivision
        return `${data.city}, ${state}`
      }
    }
  } catch (error) {
    console.warn("Reverse geocoding failed:", error)
  }

  return null
}

/**
 * Check if browser geolocation permission is already granted (no prompt)
 * @returns {Promise<boolean>}
 */
export async function checkGeolocationPermission() {
  if (!navigator.geolocation) {
    return false
  }

  // Check if permission is already granted (not just available)
  if (navigator.permissions) {
    try {
      const result = await navigator.permissions.query({ name: "geolocation" })
      // Only return true if already granted - don't prompt users
      return result.state === "granted"
    } catch (error) {
      // Permissions API not supported, assume not granted to avoid prompts
      return false
    }
  }

  // If permissions API not available, assume not granted to avoid prompts
  return false
}

/**
 * Enhanced location detection with multiple fallbacks:
 * 1. Server-provided location (from IP detection)
 * 2. SessionStorage cached location
 * 3. Browser geolocation (only if permission already granted - no popup)
 * 4. Default location
 *
 * Note: Browser geolocation is only used if user has already granted permission.
 * We don't prompt users automatically to avoid scaring them.
 *
 * @param {Object} serverLocation - Location from server IP detection
 * @returns {Promise<{latlng: [number, number]|null, cityState: string}>}
 */
export async function detectLocationWithFallbacks(serverLocation = null) {
  // Priority 1: Use server-provided location
  if (serverLocation?.cityState) {
    return {
      cityState: serverLocation.cityState,
      latlng: serverLocation.latlng || null
    }
  }

  // Priority 2: Check sessionStorage
  if (typeof sessionStorage !== "undefined") {
    const storedCityState = sessionStorage.getItem("cityState")
    const storedLatLng = sessionStorage.getItem("latlng")

    if (storedCityState) {
      let latlng = null
      if (storedLatLng) {
        try {
          const parsed = JSON.parse(storedLatLng)
          if (Array.isArray(parsed) && parsed.length === 2) {
            latlng = parsed
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }

      return {
        cityState: storedCityState,
        latlng
      }
    }
  }

  // Priority 3: Try browser geolocation (only if permission already granted - no popup)
  // We don't prompt users automatically to avoid scaring them
  try {
    const hasPermission = await checkGeolocationPermission()
    if (hasPermission) {
      const browserLocation = await getBrowserGeolocation()
      // Cache the result
      if (typeof sessionStorage !== "undefined") {
        if (browserLocation.cityState) {
          sessionStorage.setItem("cityState", browserLocation.cityState)
        }
        if (browserLocation.latlng) {
          sessionStorage.setItem("latlng", JSON.stringify(browserLocation.latlng))
        }
      }
      return browserLocation
    }
  } catch (error) {
    // User denied permission or geolocation failed
    console.debug("Browser geolocation not available:", error.message)
  }

  // Priority 4: Default location
  return {
    cityState: "97703", // Default zip code
    latlng: null
  }
}
